import { StripeV2SubscriptionPrice } from '@/services/stripe/dto';

export type AxonItem = {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
};

/**
 * Converts a Stripe plan to Axon item format
 */
export const convertPlanToAxonItem = (
  plan: StripeV2SubscriptionPrice,
): AxonItem => {
  return {
    item_id: plan.id,
    item_name: plan.label,
    price: plan.price,
    quantity: 1,
  };
};

/**
 * Converts an array of Stripe plans to Axon items format
 */
export const convertPlansToAxonItems = (
  plans: StripeV2SubscriptionPrice[],
): AxonItem[] => {
  return plans.map(convertPlanToAxonItem);
};

/**
 * Calculates the total value of plans (sum of price × quantity for each item)
 */
export const calculatePlansValue = (
  plans: StripeV2SubscriptionPrice[],
): number => {
  return plans.reduce((sum, plan) => sum + plan.price, 0);
};

export type AxonEventPayload = {
  items: AxonItem[];
  currency: string;
  value: number;
  user_data?: {
    user_id: string;
  };
};

/**
 * Creates Axon event payload for a single plan
 */
export const createAxonPayloadForPlan = (
  plan: StripeV2SubscriptionPrice,
): AxonEventPayload => {
  const item = convertPlanToAxonItem(plan);

  return {
    items: [item],
    currency: plan.currency,
    value: plan.price,
  };
};

/**
 * Creates Axon event payload for multiple plans
 */
export const createAxonPayloadForPlans = (
  plans: StripeV2SubscriptionPrice[],
): AxonEventPayload => {
  const items = convertPlansToAxonItems(plans);
  const value = calculatePlansValue(plans);

  return {
    items,
    currency: plans[0]?.currency || 'USD',
    value,
  };
};

export type AxonPurchasePayload = {
  currency: string;
  items: AxonItem[];
  shipping: number;
  tax: number;
  transaction_id: string;
  value: number;
  user_data?: {
    user_id: string;
  };
};

/**
 * Creates Axon purchase event payload from subscription data
 */
export const createAxonPurchasePayload = (
  plan: StripeV2SubscriptionPrice,
  transactionId: string,
  price: string,
  currency: string,
): AxonPurchasePayload => {
  const value = parseFloat(price);
  const item = convertPlanToAxonItem(plan);

  return {
    currency,
    items: [item],
    shipping: 0,
    tax: 0,
    transaction_id: transactionId,
    value,
  };
};
